import type { ConfigChangeArea, ConfigChangeSummary } from '../types.js'
import { ACTOR_KIND_LABEL, AREA_LABEL } from './configChangeText.js'

export const SUMMARY_PERIODS = [7, 30, 90] as const
export type SummaryPeriod = (typeof SUMMARY_PERIODS)[number]

interface ConfigChangesSummaryProps {
  summary: ConfigChangeSummary
  days: SummaryPeriod
  onDays: (days: SummaryPeriod) => void
  onArea: (area: ConfigChangeArea) => void
  onActor: (actorKey: string, label: string) => void
}

function Tile({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="border border-marine/15 bg-white px-4 py-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
      <div className="mt-1 text-2xl font-bold text-ink">{value}</div>
      {note && <div className="text-[11px] text-ink-mid">{note}</div>}
    </div>
  )
}

/** Every day of the window, so a quiet day reads as an empty column rather than a missing one. */
function daysOf(summary: ConfigChangeSummary): { date: string; count: number }[] {
  const counts = new Map(summary.byDay.map((day) => [day.date, day.count]))
  const days: { date: string; count: number }[] = []
  const end = new Date(summary.to)
  for (let at = new Date(summary.from); at <= end; at = new Date(at.getTime() + 24 * 60 * 60 * 1000)) {
    const date = at.toISOString().slice(0, 10)
    days.push({ date, count: counts.get(date) ?? 0 })
  }
  return days
}

/**
 * The panel that opens the history: how much changed in the window, by
 * day, in which areas and by whom. An area or a person narrows the list
 * below to them.
 */
export function ConfigChangesSummary({ summary, days, onDays, onArea, onActor }: ConfigChangesSummaryProps) {
  const perDay = daysOf(summary)
  const peak = Math.max(1, ...perDay.map((day) => day.count))
  const topArea = summary.byArea[0]
  return (
    <section className="mb-6 border border-marine/15 bg-marine-lt/20 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-bold text-ink">The last {days} days</h2>
        <div className="flex gap-1" role="group" aria-label="Period">
          {SUMMARY_PERIODS.map((period) => (
            <button
              key={period}
              type="button"
              onClick={() => onDays(period)}
              aria-pressed={period === days}
              className={`rounded-full border px-2.5 py-0.5 text-[11px] ${period === days ? 'border-marine bg-marine text-white' : 'border-marine/30 bg-white text-marine hover:bg-marine-lt/40'}`}
            >
              {period} days
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile label="Changes" value={summary.total.toLocaleString()} note={summary.truncated ? 'Counted from the newest 5,000' : undefined} />
        <Tile label="People who made them" value={String(summary.byActor.length)} note={summary.byActor.length === 10 ? 'The ten most active' : undefined} />
        <Tile label="Most changed" value={topArea ? AREA_LABEL[topArea.area] : '—'} note={topArea ? `${topArea.count} changes` : undefined} />
      </div>
      {summary.total > 0 && (
        <>
          <div className="mt-4 flex h-16 items-end gap-px" aria-label="Changes per day">
            {perDay.map((day) => (
              <div key={day.date} className="flex-1 bg-marine/80" style={{ height: `${day.count === 0 ? 2 : Math.max(6, (day.count / peak) * 100)}%`, opacity: day.count === 0 ? 0.25 : 1 }} title={`${day.date}: ${day.count} ${day.count === 1 ? 'change' : 'changes'}`} />
            ))}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-ink-mid">By area</div>
              <ul className="space-y-1">
                {summary.byArea.map(({ area, count }) => (
                  <li key={area}>
                    <button type="button" onClick={() => onArea(area)} className="flex w-full justify-between text-left text-xs text-ink hover:text-marine">
                      <span className="underline decoration-marine/30 underline-offset-2">{AREA_LABEL[area]}</span>
                      <span className="text-ink-mid">{count}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-ink-mid">By person</div>
              <ul className="space-y-1">
                {summary.byActor.map(({ actorKey, actor, count }) => {
                  const label = actor.name ?? actor.id ?? ACTOR_KIND_LABEL[actor.kind]
                  return (
                    <li key={actorKey}>
                      <button type="button" onClick={() => onActor(actorKey, label)} className="flex w-full justify-between text-left text-xs text-ink hover:text-marine">
                        <span>
                          <span className="underline decoration-marine/30 underline-offset-2">{label}</span> <span className="text-ink-mid">· {ACTOR_KIND_LABEL[actor.kind]}</span>
                        </span>
                        <span className="text-ink-mid">{count}</span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        </>
      )}
    </section>
  )
}
